import React, { useCallback } from 'react';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import { useFormContextOptional } from '../../contexts/FormContext';
import { useApi } from '../../api/api';
import { mapLobsterAnnualResultsToFisheryData } from '../../utils/lobsterFisheryDataMapper';

const DEFAULT_HERRING = [6741, 10885, 15435];
const DEFAULT_MENHADEN = [60000, 48000, 48000];

interface LobsterParams {
  herringSupply?: number[];
  menhadenSupply?: number[];
  importUnitValue?: number;
  fuelPrice?: number;
  trapPrice?: number;
  ropePrice?: number;
  ropelessGear?: boolean;
  projectionYears?: number;
}

interface LobsterProjectionCompleteData {
  results: unknown;
  projectionId?: number;
  fisheryData: unknown[];
  dbParams: LobsterParams;
}

interface SubmitLobsterProjectionData {
  /** Override: called when user clicks Run Lobster Projection. If provided, replaces built-in logic. */
  onSubmitLobster?: () => void | Promise<void>;
  /** Disabled when loading or updating. */
  submitLobsterDisabled?: boolean;
  /** When true, button shows "Running..." instead of "Run Lobster Projection". */
  submitLobsterLoading?: boolean;
}

/**
 * Submit button for lobster projection.
 * Owns the run logic: reads params from PageDataContext, calls API, transforms results,
 * then invokes page callbacks to update state. Uses lobsterLoading for disabled/Running state.
 */
export default function SubmitLobsterProjection(props: RegistryComponentProps): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const formContext = useFormContextOptional();
  const data = getRegistryData<SubmitLobsterProjectionData>(props);
  const { runLobsterProjection, getLobsterScenario } = useApi();

  const overrideSubmit = data.onSubmitLobster;
  const pageRunLobster = pageData?.actions?.runLobsterProjection as (() => void | Promise<void>) | undefined;
  const lobsterLoading = (pageData?.data?.lobsterLoading as boolean | undefined) ?? false;
  const updatingPStar = (pageData?.data?.updatingPStar as boolean | undefined) ?? false;
  const disabled = (data.submitLobsterDisabled ?? updatingPStar ?? lobsterLoading) ?? false;
  const loading = (data.submitLobsterLoading ?? lobsterLoading) ?? false;

  const runProjection = useCallback(async () => {
    const actions = pageData?.actions as Record<string, (...args: unknown[]) => void | Promise<void>> | undefined;

    const lobsterParams: LobsterParams = {
      herringSupply: (formContext?.values?.['herring-supply'] as number[] | undefined) ?? DEFAULT_HERRING,
      menhadenSupply: (formContext?.values?.['menhaden-supply'] as number[] | undefined) ?? DEFAULT_MENHADEN,
      importUnitValue: (formContext?.values?.['bait-import-unit-value'] as number | undefined) ?? 0.18,
      fuelPrice: (formContext?.values?.['fuel-price'] as number | undefined) ?? 3.0,
      trapPrice: (formContext?.values?.['trap-price'] as number | undefined) ?? 180.0,
      ropePrice: (formContext?.values?.['rope-price'] as number | undefined) ?? 0.5,
      ropelessGear: (formContext?.values?.['ropeless-gear-technology'] as boolean | undefined) ?? false,
      projectionYears: (formContext?.values?.['lobster-projection-years'] as number | undefined) ?? 3,
    };

    const setLobsterLoading = actions?.setLobsterLoading as ((loading: boolean) => void) | undefined;
    const setRecalculationMessage = actions?.setRecalculationMessage as ((msg: string) => void) | undefined;
    const captureBaseline = actions?.captureBaseline as (() => void) | undefined;
    const onLobsterProjectionComplete = actions?.onLobsterProjectionComplete as ((data: LobsterProjectionCompleteData) => void) | undefined;

    try {
      setLobsterLoading?.(true);
      setRecalculationMessage?.('Running lobster projection...');
      captureBaseline?.();

      console.log('[LOBSTER SubmitLobsterProjection] runProjection starting', { lobsterParams });
      const response = await runLobsterProjection({
        scenario_name: `Lobster_${Date.now()}`,
        bait_market: {
          herring_supply_mt: lobsterParams.herringSupply,
          menhaden_supply_mt: lobsterParams.menhadenSupply,
          import_unit_value: lobsterParams.importUnitValue,
        },
        opex: {
          fuel_price_per_gal: lobsterParams.fuelPrice,
        },
        gear: {
          trap_price: lobsterParams.trapPrice,
          rope_price_per_ft: lobsterParams.ropePrice,
          ropeless: lobsterParams.ropelessGear,
        },
        simulation: {
          years: lobsterParams.projectionYears,
        },
      });

      if (response?.data?.projection_id) {
        console.log('[LOBSTER SubmitLobsterProjection] runLobsterProjection response', { response, projectionId: response.data.projection_id });
        const scenarioData = await getLobsterScenario(response.data.projection_id);
        console.log('[LOBSTER SubmitLobsterProjection] getLobsterScenario result', { hasData: !!scenarioData?.data });
        const results = scenarioData.data;

        let fisheryData: unknown[] = [];
        if (results?.annual_results) {
          fisheryData = mapLobsterAnnualResultsToFisheryData(results.annual_results);
        }

        const dbParams: LobsterParams = {
          herringSupply: [...(lobsterParams.herringSupply ?? [])],
          menhadenSupply: [...(lobsterParams.menhadenSupply ?? [])],
          importUnitValue: lobsterParams.importUnitValue,
          fuelPrice: lobsterParams.fuelPrice,
          trapPrice: lobsterParams.trapPrice,
          ropePrice: lobsterParams.ropePrice,
          ropelessGear: lobsterParams.ropelessGear,
        };

        onLobsterProjectionComplete?.({
          results,
          projectionId: response.data.projection_id,
          fisheryData,
          dbParams,
        });
        setRecalculationMessage?.('Lobster projection completed!');
        setTimeout(() => setRecalculationMessage?.(''), 3000);
      }
    } catch (err) {
      console.error('Error running lobster projection:', err);
      setRecalculationMessage?.('Failed to run lobster projection');
      setTimeout(() => setRecalculationMessage?.(''), 3000);
    } finally {
      setLobsterLoading?.(false);
    }
  }, [pageData, formContext, runLobsterProjection, getLobsterScenario]);

  const onSubmit = overrideSubmit ?? pageRunLobster ?? runProjection;

  const handleClick = useCallback(() => {
    if (typeof onSubmit === 'function') {
      void Promise.resolve(onSubmit());
    }
  }, [onSubmit]);

  return (
    <div className="noscroll-button-container" style={{ marginTop: '30px' }}>
      <button
        className="btn-primary noscroll-recalculate-all-button"
        onClick={handleClick}
        disabled={disabled}
      >
        {loading ? 'Running...' : 'Run Lobster Projection'}
      </button>
    </div>
  );
}
